class AthleteFacets extends HTMLElement {
  constructor() {
    super();
    this.state = {
      fetched_athletes: null,
      athletes: [],
    };

    this.athleteCountId = '#athleteCount';
    this.athleteFiltersCountId = '#athleteFiltersCount';
    this.athleteResultsCountId = '#athleteResultsCount';
    this.athleteClearAllFiltersId = '#athleteClearAllFilters';
    this.athleteGridJsId = '#athlete-grid-js';
    this.facetCheckboxInputsSelector = ".facets-layout input[type='checkbox']";
    this.facetCloseButton = this.querySelector('button.mobile-facets__close-button');
    this.facetsPillsForm = this.querySelector('#FacetFiltersPillsForm .active-facets');
    this.storefrontApiToken = this.getAttribute('data-storefront-api-token');
    this.cacheDays = this.getAttribute('data-cache-days');
    this.storeUrl = window.Shopify.shop;
    this.allAthletesQuery = this.getAllAthletesQuery();
    this.allowedFilters = ['sport', 'nationality'];
  }

  connectedCallback() {
    this.getCachedAthletes();
    this.setInitialCheckboxStates();
    this.attachCheckboxListeners();
    this.attachClearAllListener();
    this.updateActiveFacetsButtons();
    this.updateActiveFiltersCount();
    this.attachFacetCloseButtonListener();

    let previousUrl = '';
    const observer = new MutationObserver(() => {
      if (window.location.href !== previousUrl) {
        previousUrl = window.location.href;
        this.filterAthletes();
      }
    });
    const config = { subtree: true, childList: true };

    observer.observe(document, config);
  }

  attachFacetCloseButtonListener() {
    if (this.facetCloseButton) {
      this.facetCloseButton.addEventListener('click', (e) => {
        e.preventDefault();
      });
    }
  }

  getAllAthletesQuery() {
    return `
      query AllAthletes($first: Int!, $cursor: String) {
        metaobjects(type: "athlete", first: $first, after: $cursor) {
          nodes {
            handle
            name: field(key: "name") {
              value
            }
            profile_image: field(key: "profile_image") {
              value
              reference {
                ... on MediaImage {
                  id
                  image {
                    url
                    altText
                  }
                  presentation {
                    asJson(format: IMAGE)
                  }
                }
              }
            }
            action_image: field(key: "action_image") {
              value
              reference {
                ... on MediaImage {
                  id
                  image {
                    url
                    altText
                  }
                  presentation {
                    asJson(format: IMAGE)
                  }
                }
              }
            }
            sport: field(key: "sport") {
              reference {
                ... on Metaobject {
                  handle
                  sport_name: field(key: "sport_name") {
                    value
                  }
                }
              }
            }
            nationality: field(key: "nationality") {
              reference {
                ... on Metaobject {
                  handle
                  country_name: field(key: "country_name") {
                    value
                  }
                  flag_image: field(key: "flag_image") {
                    reference {
                      ... on MediaImage {
                        image {
                          url
                          altText
                        }
                      }
                    }
                  }
                }
              }
            }
            featured: field(key: "featured") {
              value
            }
            sort_value: field(key: "sort") {
              value
            }
            onlineStoreUrl
          }
          pageInfo {
            hasNextPage
            endCursor
            hasPreviousPage
            startCursor
          }
        }
      }
    `;
  }

  async getCachedAthletes() {
    const cacheKey = 'athletes_data';
    const cacheDays = Number(this.cacheDays);
    const cacheDuration =
      cacheDays > 0
        ? cacheDays * 24 * 60 * 60 * 1000 // X days in milliseconds
        : 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
    const cachedData = JSON.parse(localStorage.getItem(cacheKey));
    const currentTime = Date.now();

    if (cachedData && cachedData.timestamp + cacheDuration > currentTime) {
      console.log('Using cached athletes data');
      this.setState('fetched_athletes', cachedData.athletes);
      this.filterAthletes();
    } else {
      console.log('Cached athletes data is outdated or does not exist. Fetching new data.');
      localStorage.removeItem(cacheKey); // Clear outdated data
      await this.fetchAllAthletes(); // Fetch new athletes and update cache
    }
  }

  async fetchAllAthletes() {
    const variables = { first: 50, cursor: null };
    let hasNextPage = true;
    let allAthletes = [];

    while (hasNextPage) {
      const athletesOptionsQuery = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontApiToken,
        },
        body: JSON.stringify({ query: this.allAthletesQuery, variables }),
      };

      try {
        const response = await fetch(`https://${this.storeUrl}/api/2025-01/graphql`, athletesOptionsQuery);
        const result = await response.json();

        if (result.errors) {
          console.error('GraphQL error:', result.errors);
          return;
        }

        const { nodes, pageInfo } = result.data.metaobjects;

        if (nodes && nodes.length > 0) {
          allAthletes = [...allAthletes, ...nodes];
        }

        hasNextPage = pageInfo?.hasNextPage;
        variables.cursor = pageInfo?.endCursor || null;
      } catch (error) {
        console.error('Error fetching athletes:', error);
        return;
      }
    }

    this.setState('fetched_athletes', allAthletes);
    this.filterAthletes();

    // Cache the new data with a timestamp
    const currentTime = Date.now();
    localStorage.setItem(
      'athletes_data',
      JSON.stringify({
        athletes: allAthletes,
        timestamp: currentTime,
      }),
    );
  }

  filterAthletes() {
    const urlParams = new URLSearchParams(window.location.search);
    const sports = urlParams.get('sport')?.split(',') || [];
    const nationalities = urlParams.get('nationality')?.split(',') || [];

    const filteredAthletes = this.state.fetched_athletes?.filter((athlete) => {
      const athleteSport = athlete.sport?.reference?.handle;
      const athleteNationality = athlete.nationality?.reference?.handle;

      const sportMatches = sports.length === 0 || sports.includes(athleteSport);
      const nationalityMatches = nationalities.length === 0 || nationalities.includes(athleteNationality);

      return sportMatches && nationalityMatches;
    });

    // Sort athletes by sort_value from high to low, with null values sorted alphabetically by name
    if (filteredAthletes && filteredAthletes.length > 0) {
      filteredAthletes.sort((a, b) => {
        const aValue = a.sort_value?.value ? parseFloat(a.sort_value.value) : null;
        const bValue = b.sort_value?.value ? parseFloat(b.sort_value.value) : null;

        // If both are null, sort alphabetically based on name
        if (aValue === null && bValue === null) {
          return a.name.value.localeCompare(b.name.value);
        }

        // If only one is null, put the null value at the bottom
        if (aValue === null) return 1;
        if (bValue === null) return -1;

        // Sort from high to low
        return bValue - aValue;
      });
    }

    this.setState('athletes', filteredAthletes || []);

    this.renderAthletesNumber();
    this.renderCustomLayoutContent();
    this.hideEmptyFacetItems();
  }

  renderAthletesNumber() {
    const { athletes } = this.state;

    const athleteCount = this.querySelector(`${this.athleteCountId}`);
    if (athleteCount) {
      athleteCount.textContent = `${athletes.length}`;
    }

    const athleteResultsCountId = this.querySelector(`${this.athleteResultsCountId}`);
    if (athleteResultsCountId) {
      athleteResultsCountId.textContent = `${athletes.length}`;
    }
  }

  renderCustomLayoutContent() {
    const { athletes } = this.state;
    const athleteLayout = document.querySelector(this.athleteGridJsId);
    const noResultsBlock = document.querySelector('.athlete-grid__no-results');

    if (!athleteLayout) {
      console.error('Athlete layout container not found.');
      return;
    }

    // Clear existing content before adding new items
    athleteLayout.innerHTML = '';

    if (athletes.length === 0) {
      if (noResultsBlock) {
        noResultsBlock.style.display = 'block';
      }
    } else {
      if (noResultsBlock) {
        noResultsBlock.style.display = 'none';
      }

      athletes.forEach((athlete, index) => {
        // Create a new <li> element
        const li = document.createElement('li');
        li.className =
          athlete.featured?.value == 'true'
            ? 'grid__item scroll-trigger animate--slide-in athlete-card--featured'
            : 'grid__item scroll-trigger animate--slide-in';

        // Construct the inner HTML using the athlete data
        li.innerHTML = `
        <div class="athlete-card__wrapper card card--standard card--media">
          <div class="card__inner ratio" style="--ratio-percent: 111.11111111111111%;">
            <a
              href="${athlete.onlineStoreUrl}"
              class="full-unstyled-link athlete-card__link"
              aria-label="View ${athlete.name.value} athlete"
            >
              <div class="athlete-card__image-wrapper card__media">
                <div class="media media--adapt">
                  <img
                    src="${athlete.profile_image.reference.image.url}&width=1320"
                    alt="${athlete.profile_image.reference.image.altText || ''}"
                    class="athlete-card__profile-image"
                    loading="lazy"
                    width="1320"
                    style="object-position: ${athlete.profile_image.reference?.presentation?.asJson?.focalPoint?.x * 100 || '50'}% ${athlete.profile_image.reference?.presentation?.asJson?.focalPoint?.y * 100 || '50'}%"
                  />
                  <img
                    src="${athlete.action_image.reference.image.url}&width=1320"
                    alt="${athlete.action_image.reference.image.altText || ''}"
                    class="athlete-card__action-image"
                    width="1320"
                    loading="lazy"
                    style="object-position: ${athlete.action_image.reference?.presentation?.asJson?.focalPoint?.x * 100 || '50'}% ${athlete.action_image.reference?.presentation?.asJson?.focalPoint?.y * 100 || '50'}%"
                  />
                </div>
              </div>
            </a>
          </div>
          <div class="card__content">
            <a
              href="${athlete.onlineStoreUrl}"
              class="full-unstyled-link athlete-card__link"
            >
              <h5 class="athlete_name">${athlete.name.value}</h5>
              <div class="athlete-card__info">
                <div class="athlete-card__country">
                  <div class="athlete-card__flag-image">
                    <img
                      src="${athlete.nationality.reference.flag_image.reference.image.url}&width=96"
                      alt="${athlete.nationality.reference.flag_image.reference.image.altText || ''}"
                      width="96"
                      class="flag-image"
                      loading="lazy"
                    />
                  </div>
                  <p class="athlete-card__details">
                    ${athlete.nationality.reference.country_name.value} |
                    ${athlete.sport.reference.sport_name.value}
                  </p>
                </div>
              </div>
            </a>
          </div>
        </div>
      `;

        // Append the <li> to the athlete layout
        athleteLayout.appendChild(li);
      });
    }
  }

  hideEmptyFacetItems() {
    const { fetched_athletes } = this.state;

    const existingNationalities = new Set(
      fetched_athletes.map((athlete) => athlete?.nationality?.reference?.country_name?.value),
    );
    const existingSports = new Set(fetched_athletes.map((athlete) => athlete?.sport?.reference?.sport_name?.value));

    document.querySelectorAll('.mobile-facets__item[data-nationality]').forEach((item) => {
      const nationality = item.getAttribute('data-nationality');
      if (!existingNationalities.has(nationality)) {
        item.style.display = 'none';
      }
    });

    // Hide sports that aren't present in athletes
    document.querySelectorAll('.mobile-facets__item[data-sport]').forEach((item) => {
      const sport = item.getAttribute('data-sport');
      if (!existingSports.has(sport)) {
        item.style.display = 'none';
      }
    });
  }

  setInitialCheckboxStates() {
    const urlParams = new URLSearchParams(window.location.search);
    this.querySelectorAll(this.facetCheckboxInputsSelector).forEach((checkbox) => {
      const key = checkbox.closest('.facets-layout').dataset.name;
      if (urlParams.has(key) && urlParams.get(key).split(',').includes(checkbox.name)) {
        checkbox.checked = true;
      }
    });
  }

  attachCheckboxListeners() {
    this.querySelectorAll(this.facetCheckboxInputsSelector).forEach((checkbox) => {
      checkbox.addEventListener('change', (e) => this.handleCheckboxChange(e.target));
    });
  }

  handleCheckboxChange(checkbox) {
    const key = checkbox.closest('.facets-layout').dataset.name;
    const urlParams = new URLSearchParams(window.location.search);
    const values = urlParams.has(key) ? urlParams.get(key).split(',') : [];

    if (checkbox.checked) {
      values.push(checkbox.name);
    } else {
      const index = values.indexOf(checkbox.name);
      if (index > -1) values.splice(index, 1);
    }

    if (values.length > 0) {
      urlParams.set(key, values.join(','));
    } else {
      urlParams.delete(key);
    }

    history.replaceState(null, '', `?${urlParams.toString()}`);
    this.updateActiveFacetsButtons();
    this.updateActiveFiltersCount();
  }

  attachClearAllListener() {
    const clearAllBtn = this.querySelector(this.athleteClearAllFiltersId);
    clearAllBtn?.addEventListener('click', () => {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.forEach((_, key) => urlParams.delete(key));
      history.replaceState(null, '', `?`);
      this.querySelectorAll(this.facetCheckboxInputsSelector).forEach((checkbox) => (checkbox.checked = false));
      this.updateActiveFacetsButtons();
      this.updateActiveFiltersCount();
    });
  }

  updateActiveFiltersCount() {
    const athleteFiltersCount = this.querySelector(`${this.athleteFiltersCountId}`);
    const urlParams = new URLSearchParams(window.location.search);

    let activeFilterCount = 0;

    urlParams.forEach((value, key) => {
      if (this.allowedFilters.includes(key)) {
        if (value.includes(',')) {
          activeFilterCount += value.split(',').length;
        } else {
          activeFilterCount += 1;
        }
      }
    });

    if (activeFilterCount > 0) {
      athleteFiltersCount.textContent = `(${activeFilterCount})`;
    } else {
      athleteFiltersCount.textContent = '';
    }
  }

  updateActiveFacetsButtons() {
    if (!this.facetsPillsForm) return;
    const urlParams = new URLSearchParams(window.location.search);

    // Clear existing buttons
    this.facetsPillsForm.innerHTML = '';

    let hasActiveFilters = false;

    // Add new buttons
    urlParams.forEach((value, key) => {
      if (this.allowedFilters.includes(key)) {
        value.split(',').forEach((filterValue) => {
          hasActiveFilters = true;
          const formattedText = this.formatFilterText(filterValue);
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'active-facets__button active-facets__button--light';
          button.innerHTML = `
          <span class="active-facets__button-inner">
          <span class="svg-wrapper" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" class="icon icon-close-small" width="11.999" height="12" viewBox="0 0 11.999 12">
            <path d="M11.827,10.995l-5-5,5-4.994a.589.589,0,0,0-.832-.832L6,5.167,1.005.173a.589.589,0,0,0-.834.832L5.167,6l-5,5a.589.589,0,0,0,.834.832L6,6.832l5,5a.589.589,0,0,0,.832-.832" transform="translate(0)"></path>
          </svg></span>
            ${formattedText}
            <span class="visually-hidden">Remove filter</span>
          </span>
        `;
          button.addEventListener('click', () => this.removeFilter(key, filterValue));
          this.facetsPillsForm.appendChild(button);
        });
      }
    });

    if (hasActiveFilters) {
      this.facetsPillsForm.classList.remove('active-facets--empty');
    } else {
      this.facetsPillsForm.classList.add('active-facets--empty');
    }
  }

  formatFilterText(value) {
    // Replace hyphens with spaces, capitalize each word
    return value
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  removeFilter(key, value) {
    const urlParams = new URLSearchParams(window.location.search);
    const values = urlParams
      .get(key)
      .split(',')
      .filter((v) => v !== value);
    if (values.length > 0) {
      urlParams.set(key, values.join(','));
    } else {
      urlParams.delete(key);
    }

    history.replaceState(null, '', `?${urlParams.toString()}`);
    this.querySelectorAll(`.facets-layout input[name='${value}']`).forEach((checkbox) => (checkbox.checked = false));
    this.updateActiveFacetsButtons();
  }

  setState(key, value) {
    if (!Object.keys(this.state).includes(key)) return;
    this.state[key] = value;
    const event = new Event(`state.${this.id}.${key}`);
    document.dispatchEvent(event);
  }
}

customElements.define('athlete-facets', AthleteFacets);
